import type { HttpsCallable, HttpsCallableResult } from 'firebase/functions'
import type { MarketingPermissions, UserAddress, UserDetails, UserDetailsBase, UserInfo, UserOrder, Wishlist } from '~/types/user'
// import type { WishlistItem } from '~/types/item'

// Firebase comes from $firebase() (plugins/firebase.client.ts), loaded on first use: no static SDK imports here,
// this composable is used by the user store that every page loads
export const useFirebase = () => {
  const { $firestore, $firebase } = useNuxtApp()
  const createUserDocument = async (uid: string, userDetails: UserDetailsBase): Promise<any> => {
    const fs = await $firestore()
    await fs.setDoc(fs.doc(fs.db, 'users', uid), userDetails).then(response => console.log(response)).catch(error => console.error(error))
  }

  const updateUserDocument = async (userDetails: UserDetailsBase): Promise<boolean> => {
    const sanitizedUserDetails: UserInfo = {
      firstname: userDetails.firstname,
      lastname: userDetails.lastname,
      phone: userDetails.phone,
      dob: userDetails.dob
    }
    const [fs, { auth }] = await Promise.all([$firestore(), $firebase()])
    const docRef = fs.doc(fs.db, 'users', auth.currentUser!.uid)
    return await fs.updateDoc(docRef, { ...sanitizedUserDetails })
      .then(() => true)
      .catch((err: any) => {
        console.error('User document update failed', err)
        return false
      })
  }

  const getMarketingPermissionsStatusHandler = async (): Promise<MarketingPermissions | undefined> => {
    const { auth, functions, functionsSdk } = await $firebase()
    if (auth.currentUser) {
      const getMarketingPermissions: HttpsCallable<unknown, MarketingPermissions | null> = functionsSdk.httpsCallable(functions, 'tkapi-customer-getMarketingPermissionStatus')
      try {
        const response: HttpsCallableResult<MarketingPermissions | null> = await getMarketingPermissions()
        if (response.data) {
          return response.data
        }
      }
      catch (e) {
        console.error(e)
        throw new Error('Could not get marketing permissions.', { cause: e })
      }
    }
    else {
      return undefined
    }
  }

  const getUserDetailsWithSubcollections = async (): Promise<UserDetails | null> => {
    const { auth, functions, functionsSdk } = await $firebase()
    if (auth.currentUser) {
      const getUser = functionsSdk.httpsCallable(functions, 'tkapi-customer-getUser')
      return await getUser()
        .then((response: any) => {
          return response.data.response
        })
        .catch((err: any) => {
          // Sentry.captureException(err)
          console.error(err)
          return null
        })
    }
    return null
  }

  const updateUserMarketingPermissions = async (marketingPermissions: MarketingPermissions) => {
    const [fs, { auth }] = await Promise.all([$firestore(), $firebase()])
    const docRef = fs.doc(fs.db, 'users', auth.currentUser!.uid)
    return await fs.updateDoc(docRef, { marketingPermissions: { ...marketingPermissions } })
      .then(() => true)
      .catch((err: any) => {
        console.error(err)
        return false
      })
  }

  const getUserOrders = async (): Promise<UserOrder[]> => {
    const { auth, functions, functionsSdk } = await $firebase()
    if (auth.currentUser) {
      const getOrders = functionsSdk.httpsCallable(functions, 'tkapi-customer-getOrders')
      return getOrders()
        .then((response: any) => {
          return response.data.orders
        })
        .catch((err: any) => {
          // Sentry.captureException(err)
          console.error(err)
          return []
        })
    }
    return []
  }

  const addWishlist = async (name?: string): Promise<Wishlist | null> => {
    console.log('add wishlist ', name)
    const { auth } = await $firebase()
    if (!auth.currentUser?.uid) {
      console.error('User not found!')
      return Promise.reject(null)
    }
    const createdAt = new Date()
    const fs = await $firestore()
    const docRef = fs.doc(fs.db, 'users', auth.currentUser.uid)
    const wishlistCollection = fs.collection(docRef, 'wishlists')

    const wishlist: Wishlist = {
      name: name || 'Toivelista',
      items: [],
      createdAt: createdAt.toISOString(),
      updatedAt: createdAt.toISOString()
    }
    return fs.addDoc(wishlistCollection, wishlist)
      .then((response) => {
        return Promise.resolve({ ...wishlist, id: response.id })
      })
      .catch((err: any) => {
        console.error(err)
        return Promise.reject(null)
      })
  }

  const updateWishlist = async (wishlist: Wishlist): Promise<void> => {
    const croppedWishlist = { ...wishlist }
    const updatedAt = new Date()
    croppedWishlist.updatedAt = updatedAt.toISOString()
    const [fs, { auth }] = await Promise.all([$firestore(), $firebase()])
    const docRef = fs.doc(fs.db, 'users', auth.currentUser!.uid, 'wishlists', wishlist.id!)

    return await fs.updateDoc(docRef, croppedWishlist)
      .catch((error: any) => {
        console.error(error)
        console.error(`Updating wishlist '${croppedWishlist.name}' failed!`)
        return Promise.reject()
      })
  }

  const deleteWishlist = async (wishlistId: string): Promise<void> => {
    const [fs, { auth }] = await Promise.all([$firestore(), $firebase()])
    const docRef = fs.doc(fs.db, 'users', auth.currentUser!.uid, 'wishlists', wishlistId)
    return await fs.deleteDoc(docRef)
      .catch(() => {
        console.error(`Deleting wishlist with ID '${wishlistId}' failed!`)
        return Promise.reject()
      })
  }

  const addAddress = async (address: UserAddress): Promise<any> => {
    const [fs, { auth }] = await Promise.all([$firestore(), $firebase()])
    const docRef = fs.doc(fs.db, 'users', auth.currentUser!.uid)
    const addressCollection = fs.collection(docRef, 'addresses')
    return await fs.addDoc(addressCollection, address)
      .catch((err: any) => {
        console.error('addAddress', err)
        return null
      })
  }

  const updateAddress = async (address: UserAddress): Promise<boolean> => {
    const [fs, { auth }] = await Promise.all([$firestore(), $firebase()])
    const docRef = fs.doc(fs.db, 'users', auth.currentUser!.uid, 'addresses', address.id!)
    return await fs.updateDoc(docRef, { ...address })
      .then(() => true)
      .catch((err: any) => {
        console.error('updateAddress', err)
        return false
      })
  }

  const verifyBeforeUpdateEmail = async (email: string): Promise<boolean> => {
    const { auth, authSdk } = await $firebase()
    if (auth.currentUser) {
      console.log('new email:', email)
      return await authSdk.verifyBeforeUpdateEmail(auth.currentUser, email)
        .then(() => {
          // Update email to store and on the Firestore-document
          // const store = useStore()
          // const { updateUserDocument } = useFirestore($fire)
          // const details = store.getters[UserGetters.details]
          // details.email = email
          // store.dispatch(UserActions.setUserDetails, details)
          // await updateUserDocument(details)
          return true
        })
        .catch((err: any) => {
          console.error('updateAuthEmail', err)
          return false
        })
    }
    else {
      return false
    }
  }

  const reauthenticateUser = async (psw: string): Promise<boolean> => {
    const { auth, authSdk } = await $firebase()
    if (!auth.currentUser || auth.currentUser?.email === null) {
      auth.signOut()
      return false
    }

    // https://github.com/nuxt-community/firebase-module/issues/20
    const creds = authSdk.EmailAuthProvider.credential(
      auth.currentUser.email,
      psw
    )
    return await authSdk.reauthenticateWithCredential(auth.currentUser, creds)
      .then(() => true)
      .catch((err: any) => {
        console.error('reauthenticateWithCredential', err)
        return false
      })
  }

  const updateEmail = async (newEmail: string) => {
    const { auth, authSdk } = await $firebase()
    return authSdk.updateEmail(auth.currentUser!, newEmail)
      .then((response) => {
        console.log(response)
        // Email updated!
        console.log('Email updated successfully')
        return true
      })
      .catch((error) => {
        // An error occurred
        console.error('Error updating email:', error)
        return false
      })
  }

  const updateAuthPassword = async (form: any): Promise<boolean> => {
    const updatePasswordHandler = async (psw: string, newPsw: string) => {
      return await reauthenticateUser(psw)
        .then(async (success) => {
          if (success) {
            const { auth, authSdk } = await $firebase()
            return authSdk.updatePassword(auth.currentUser!, newPsw)
              .then(() => {
                return true
              })
              .catch(() => {
                return false
              })
          }
          else {
            return false
          }
        })
    }
    return updatePasswordHandler(form.oldPassword, form.password)
  }

  const removeAddress = async (id: string): Promise<boolean> => {
    const [fs, { auth }] = await Promise.all([$firestore(), $firebase()])
    const docRef = fs.doc(fs.db, 'users', auth.currentUser!.uid, 'addresses', id)
    return await fs.deleteDoc(docRef)
      .then(() => true)
      .catch((err: any) => {
        console.error('removeAddress', err)
        return false
      })
  }

  return {
    createUserDocument,
    getUserDetailsWithSubcollections,
    updateUserDocument,
    updateUserMarketingPermissions,
    getUserOrders,
    addWishlist,
    updateWishlist,
    deleteWishlist,
    updateAddress,
    removeAddress,
    addAddress,
    getMarketingPermissionsStatusHandler,
    verifyBeforeUpdateEmail,
    reauthenticateUser,
    updateEmail,
    updateAuthPassword
  }
}
